import DefaultButton from 'components/buttons/DefaultButton/DefaultButton';
import { ITruckManufacturers, ITruckModels, ITruckFuel, ITruckVersion } from 'interfaces/truckSelector';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { getCurrentCatalog } from 'store/selectors/catalogs.selector';
import { useDeviceDetect } from 'utils/hooks/deviceDetect';
import useHttp from 'utils/hooks/useHttp';
import '../VehicleContent.scss';
import { TRUCK_IDENTIFIER } from 'utils/constants/defaultConstants';

type Props = {};

const CatalogTruckSelector: React.FC<Props> = () => {
  const { control, getValues, handleSubmit, setValue } = useForm();
  const http = useHttp();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useDeviceDetect();
  const currentCatalog = useSelector(getCurrentCatalog);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const [manufacturers, setManufacturers] = useState<ITruckManufacturers[]>([]);
  const [manufacturersLoading, setManufacturersLoading] = useState(false);

  const [models, setModels] = useState<ITruckModels[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  const [fuelType, setFuelType] = useState<ITruckFuel[]>([]);
  const [fuelLoading, setFuelLoading] = useState(false);

  const [variants, setVariants] = useState<ITruckVersion[]>([]);
  const [variantsLoading, setVariantsLoading] = useState(false);

  function getData(request, setLoading) {
    setLoading(true);

    return request
      .then((res) => res.data)
      .finally(() => {
        setLoading(false);
      });
  }

  const manufacturerOptions = () => {
    return (
      manufacturers?.map((item) => ({
        label: item.description,
        value: item.id,
      })) || []
    );
  };

  const modelOptions = () => {
    return (
      models?.map((item) => ({
        label: item.description,
        value: item.id,
      })) || []
    );
  };

  const fuelOptions = () => {
    return (
      fuelType?.map((item) => ({
        label: item.description,
        value: item.id,
      })) || []
    );
  };

  const versionOptions = () => {
    return (
      variants?.map((item) => ({
        label: `${item.model_name} ${item.name} ${item.engine_capacity} ${item.weight} ${item.start_year}/${item.start_month} - ${item.end_year}/${item.end_month} ${item.power_hp_from} ${!item.power_hp_to ? '' : ` - ${item.power_hp_to}`} HP ${item.power_kw_from} - ${!item.power_kw_to ? '' : ` - ${item.power_kw_to}`} KW`,
        value: item.id,
      })) || []
    );
  };

  const handleManufacturerChange = (id) => {
    getData(http.truckSelector.getCatalogTruckModels(id, String(currentCatalog?.id)), setModelsLoading).then((models) => setModels(models));
    setValue('model', '');
    setValue('fuel_type', '');
    setValue('version', '');
    setModels([]);
    setFuelType([]);
    setVariants([]);
    setSubmitDisabled(true);
  };

  const handleModelsChange = (id) => {
    getData(http.truckSelector.getCatalogTruckFuel(id, String(currentCatalog?.id)), setFuelLoading).then((fuel) => setFuelType(fuel));
    getData(http.truckSelector.getCatalogTruckVersion(id, String(currentCatalog?.id)), setVariantsLoading).then((variant) => setVariants(variant));
    setValue('fuel_type', '');
    setValue('version', '');
    setVariants([]);
    setSubmitDisabled(true);
  };

  useEffect(() => {
    getData(http.truckSelector.getTruckManufacturers(), setManufacturersLoading).then((manufacturers) => {
      setManufacturers(manufacturers);
    });
  }, []);

  const submit = () => {
    const searchParams = createSearchParams([
      ['manufacturer_id', String(getValues().manufacturer.value)],
      ['page', '1'],
      ['model_id', String(getValues().model.value)],
      ['ktypnr', String(getValues().version?.value)],
      ['vehicle_identifier', String(TRUCK_IDENTIFIER)],
      ['source', 'T']
    ]);

    // if (currentCatalog?.catalog_type === 'TECDOC_SUPPLIER') {
    //   searchParams.append('ktypnr', String(getValues().version?.value));
    // }


    navigate({
      pathname: generatePath('/catalog/:id/categories', {
        id: String(currentCatalog?.id),
      }),
      search: `?${searchParams}`,
    });
  };

  return (
    <form className="dropdowns-container" onSubmit={handleSubmit(submit)}>
      <div className="dropdown-item">
        <label className="dropdown-label">{t('_manufacturer')}</label>
        <Controller
          control={control}
          name="manufacturer"
          render={({ field: { onChange, value } }) => (
            <Select
              value={value}
              className={`select-dropdown ${!manufacturers.length ? 'disabled' : ''}`}
              onChange={(val) => {
                handleManufacturerChange(val?.value);
                onChange(val);
              }}
              options={manufacturerOptions()}
              isSearchable={!isMobile && !isTablet}
              placeholder={manufacturersLoading ? t('_loading') : t('_manufacturer')}
              styles={{
                menu: provided => ({ ...provided, zIndex: 9999 })
              }}
            />
          )}
        />
      </div>
      <div className="dropdown-item">
        <label className="dropdown-label">{t('_model')}</label>
        <Controller
          control={control}
          name="model"
          render={({ field: { onChange, value } }) => (
            <Select
              value={value}
              className={`select-dropdown ${!models.length ? 'disabled' : ''}`}
              onChange={(val) => {
                onChange(val);
                handleModelsChange(val?.value);
              }}
              options={modelOptions()}
              isSearchable={!isMobile && !isTablet}
              placeholder={modelsLoading ? t('_loading') : t('_model')}
              styles={{
                menu: provided => ({ ...provided, zIndex: 9999 })
              }}
            />
          )}
        />
      </div>
      {currentCatalog?.catalog_type === 'TECDOC_SUPPLIER' && (
        <React.Fragment>
          <div className="dropdown-item">
            <label className="dropdown-label">{t('_fuel_type')}</label>
            <Controller
              control={control}
              name="fuel_type"
              render={({ field: { onChange, value } }) => (
                <Select
                  value={value}
                  className={`select-dropdown ${!fuelType.length ? 'disabled' : ''}`}
                  onChange={(val) => {
                    onChange(val);
                  }}
                  options={fuelOptions()}
                  isSearchable={!isMobile && !isTablet}
                  placeholder={fuelLoading ? t('_loading') : t('_fuel_type')}
                  styles={{
                    menu: provided => ({ ...provided, zIndex: 9999 })
                  }}
                />
              )}
            />
          </div>
          <div className="dropdown-item">
            <label className="dropdown-label">{t('_version')}</label>
            <Controller
              control={control}
              name="version"
              render={({ field: { onChange, value } }) => (
                <Select
                  value={value}
                  className={`select-dropdown ${!variants.length ? 'disabled' : ''}`}
                  onChange={(val) => {
                    onChange(val);
                    setSubmitDisabled(false);
                    
                  }}
                  options={versionOptions()}
                  isSearchable={!isMobile && !isTablet}
                  placeholder={variantsLoading ? t('_loading') : t('_version')}
                  styles={{
                    menu: provided => ({ ...provided, zIndex: 9999 })
                  }}
                />
              )}
            />
          </div>
        </React.Fragment>
      )}

      <div className="truck-selector-submit">
        <DefaultButton
          disabled={submitDisabled}
          text={t('_send')}
          type="submit"
        />
      </div>
    </form>
  );
};

CatalogTruckSelector.displayName = 'CatalogTruckSelector';

export default CatalogTruckSelector;
